import { useCallback, useEffect, useRef } from 'react';

import { useDebouncedCallback } from 'use-debounce';

type SerializedMutationCallbacks<TResult> = {
  /** Called when the mutation succeeds and is still the latest request */
  onSuccess?: (result: TResult) => void;
  /** Called when the mutation fails and is still the latest request */
  onError?: (error: unknown) => void;
  /** Called when starting a mutation (useful for setting loading state) */
  onStart?: () => void;
  /** Called when all mutations complete (no pending mutations left) */
  onComplete?: () => void;
};

type UseSerializedMutationOptions<TResult> = SerializedMutationCallbacks<TResult> & {
  /** Debounce timeout in milliseconds. If not provided, mutations are not debounced. */
  debounceMs?: number;
};

/**
 * A hook that serializes and debounces mutation execution to prevent stale write overwrites.
 *
 * This hook ensures that:
 * 1. Rapid changes are grouped together via debouncing (optional)
 * 2. Only one mutation is in-flight at a time
 * 3. If a new mutation is requested while one is in-flight, it queues the latest data
 * 4. Only the most recent pending mutation data is used
 * 5. Callbacks only fire for the final mutation in a sequence (intermediate mutations are silent)
 *
 * This prevents race conditions where network latency could cause an earlier mutation
 * to complete after a later one, overwriting newer data.
 *
 * @example
 * ```tsx
 * const [mutation] = useSomeMutation();
 * const { trigger, isProcessing } = useSerializedMutation(
 *   async (data: DataType) => {
 *     const result = await mutation({ variables: { input: data } });
 *     return result;
 *   },
 *   {
 *     debounceMs: 500,
 *     onSuccess: () => enqueueSnackbar('Saved', { variant: 'success' }),
 *     onError: () => enqueueSnackbar('Failed to save', { variant: 'error' }),
 *   }
 * );
 *
 * // Call trigger whenever data changes - it will debounce and serialize automatically
 * const handleChange = (newData: DataType) => {
 *   trigger(newData);
 * };
 * ```
 *
 * @param mutationFn - The async function to execute for each mutation
 * @param options - Configuration options including callbacks and debounce timeout
 * @returns An object containing the trigger function and processing state
 */
export const useSerializedMutation = <TInput, TResult>(
  mutationFn: (input: TInput) => Promise<TResult>,
  options: UseSerializedMutationOptions<TResult> = {}
) => {
  const { debounceMs, onSuccess, onError, onStart, onComplete } = options;

  // Track mutation requests to prevent stale writes from overwriting newer data.
  // Counter is incremented both when a mutation starts AND when new data is queued,
  // ensuring that intermediate mutations (those with pending data) don't fire callbacks.
  const mutationRequestCounterRef = useRef(0);
  // Track if a mutation is currently in-flight to serialize requests
  const isMutationInFlightRef = useRef(false);
  // Store the latest pending mutation data to be sent after current in-flight completes
  const pendingMutationDataRef = useRef<TInput | null>(null);
  // Store the latest executeMutation in a ref to avoid stale closure issues.
  // When executeMutation changes (due to dependency changes), the ref is updated,
  // ensuring recursive calls always use the latest version with fresh mutationFn/callbacks.
  const executeMutationRef = useRef<(input: TInput) => Promise<void>>(async () => {});

  /**
   * Execute the mutation with serialization.
   * If a mutation is already in-flight, queues this one instead.
   */
  const executeMutation = useCallback(
    async (input: TInput) => {
      // If a mutation is already in-flight, queue this one instead.
      // Increment counter here to mark any in-flight mutation as "stale" so its
      // callbacks won't fire - the user has already made newer changes.
      if (isMutationInFlightRef.current) {
        pendingMutationDataRef.current = input;
        mutationRequestCounterRef.current += 1;
        return;
      }

      isMutationInFlightRef.current = true;
      mutationRequestCounterRef.current += 1;
      const currentRequestId = mutationRequestCounterRef.current;

      try {
        onStart?.();
        const result = await mutationFn(input);

        // Only call success callback if this is still the latest request.
        // This check fails if new data was queued while this mutation was in-flight,
        // preventing misleading "saved" messages when changes are still pending.
        if (currentRequestId === mutationRequestCounterRef.current) {
          onSuccess?.(result);
        }
      } catch (error) {
        // Only call error callback if this is still the latest request
        if (currentRequestId === mutationRequestCounterRef.current) {
          onError?.(error);
        }
      } finally {
        isMutationInFlightRef.current = false;

        // Check if there's a pending mutation that came in while we were processing
        const pendingData = pendingMutationDataRef.current;
        pendingMutationDataRef.current = null;

        // Use explicit null check instead of truthy check to correctly handle falsy input values
        // (e.g., 0, "", false) that are legitimate data to be saved.
        if (pendingData !== null) {
          // Execute the pending mutation with the latest data.
          // Use executeMutationRef to ensure we call the latest version of executeMutation,
          // in case mutationFn or callbacks changed during the in-flight mutation.
          executeMutationRef.current(pendingData);
        } else if (currentRequestId === mutationRequestCounterRef.current) {
          // Only call complete callback if there's no pending mutation and this is latest
          onComplete?.();
        }
      }
    },
    [mutationFn, onComplete, onError, onStart, onSuccess]
  );

  // Keep the ref updated with the latest executeMutation
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    executeMutationRef.current = executeMutation;
  }, [executeMutation]);

  // Create debounced version if debounceMs is provided
  const debouncedExecuteMutation = useDebouncedCallback((input: TInput) => {
    executeMutation(input);
  }, debounceMs ?? 0);

  // Return the appropriate trigger based on whether debouncing is enabled
  const trigger = useCallback(
    (input: TInput) => {
      if (debounceMs !== undefined && debounceMs > 0) {
        debouncedExecuteMutation(input);
      } else {
        executeMutation(input);
      }
    },
    [debounceMs, debouncedExecuteMutation, executeMutation]
  );

  return {
    /** Trigger the mutation with the given input. Will be debounced and serialized. */
    trigger,
    /** Cancel any pending debounced mutation */
    cancel: debouncedExecuteMutation.cancel,
    /** Flush any pending debounced mutation immediately */
    flush: debouncedExecuteMutation.flush,
  };
};

export default useSerializedMutation;
